import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed, ref } from 'vue'
import domainGet from '@/helpers/domainGet'
import storeConfigs from 'constants/storeConfigs.ts'
import { Capacitor } from '@capacitor/core'
import { useAlertStore } from './alert'

type Data = { status: number; statusText: string | null }
export type Url = {
	subdomain: string
	domain: string | undefined
	tld: string
}

export const useBackendStore = defineStore(
	storeConfigs.backend.name,
	() => {
		const connection = ref(true)
		const url = ref<Url>()

		const data = ref<Data>({
			status: 200,
			statusText: null,
		})

		const status = computed(() => data.value.status)

		const useAlert = useAlertStore()

		const isConnected = computed(() => connection.value)
		const isAvailable = computed(() => data.value.status === 200)
		const isInMaintenance = computed(
			() => useAlert.failure?.data?.exception?.includes('Maintenance') === true
		)
		const isInvalidSubdomain = computed(
			() =>
				data.value.status === 403 && useAlert.failure?.error === 'no database'
		)

		async function initialise() {
			if (Capacitor.getPlatform() === 'web') {
				url.value = domainGet()
			}
		}

		function setUrl(value: string) {
			url.value = {
				subdomain: value,
				domain: undefined,
				tld: '',
			}
		}

		function connectionSet(value: boolean) {
			connection.value = value
		}

		function dataSet(value: Data) {
			// Jede erfolgreiche API-Antwort meldet {200, null}. Ohne diese Prüfung löst jede
			// Antwort Reaktivität in allem aus, was vom Backend-Status abhängt (Dashboard: Sekunden-Blockaden).
			if (
				data.value.status === value.status &&
				data.value.statusText === value.statusText
			)
				return
			data.value = value
		}

		function $reset() {
			url.value = undefined
			data.value = {
				status: 200,
				statusText: null,
			}
		}

		return {
			data,
			status,
			url,
			isConnected,
			isAvailable,
			isInMaintenance,
			isInvalidSubdomain,
			initialise,
			connectionSet,
			dataSet,
			setUrl,
			$reset,
		}
	},
	{
		persist: {
			pick: ['url'],
			storage: sessionStorage,
		},
	}
)

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useBackendStore, import.meta.hot))
}
